import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Control panel → the two activity cards (team, personal).
 *
 * **Activity here is answering the call, not playing.** Every sign-up counts
 * the same: playing, bench, "not sure", "not coming". The only thing that is
 * not activity is silence, so the cards draw exactly one distinction in ink:
 * answered (bone) against unanswered (hollow). What the answer *was* is printed
 * underneath as plain numbers, never as a second colour scale, or "not coming"
 * would read as a lesser kind of activity.
 *
 * The numbers come from `/api/activity/summary`, which shares its window and
 * rules with the inactive list (`members/inactivity.py`).
 */

type Kind = 'primary' | 'bench' | 'tentative' | 'out' | 'other'
type Kinds = Record<Kind, number>

interface SummaryMatch {
  id: string
  title: string
  start_time: number
}

export interface ActivitySummary {
  window: number
  team: {
    members: number
    answered: number
    expected: number
    kinds: Kinds
    always: number
    never: number
    matches: (SummaryMatch & { answered: number; expected: number })[]
  }
  me: {
    in_clan: boolean
    answered: number
    expected: number
    streak: number
    kinds: Kinds
    matches: (SummaryMatch & { state: 'answered' | 'silent' | 'before'; kind: Kind | ''; class_name: string })[]
    pending: SummaryMatch[]
  }
}

/* Kept for the session, like the rest of the panel's first paint (see the
   `snapshot` note in Dashboard.tsx). */
let lastSummary: ActivitySummary | null = null

export function useActivitySummary(): ActivitySummary | null {
  const [data, setData] = useState<ActivitySummary | null>(lastSummary)

  useEffect(() => {
    let alive = true
    const load = () =>
      fetch('/api/activity/summary', { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))
        .then((d: ActivitySummary | null) => {
          if (!alive || !d) return
          lastSummary = d
          setData(d)
        })
        .catch(() => {})
    load()
    const id = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  return data
}

// Bone: the same "this happened" ink the activity page uses.
const ANSWERED = '#efe0d8'

const STRIP_H = 56

function rate(a: number, e: number): string {
  return e === 0 ? '-' : `%${Math.round((a / e) * 100)}`
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-9 w-24 rounded bg-white/8" />
      <div className="h-14 rounded bg-white/6" />
      <div className="h-8 rounded bg-white/6" />
    </div>
  )
}

function Hero({ value, label, sub }: { value: string; label: string; sub: string }) {
  return (
    <div className="flex items-end gap-3">
      <p className="font-display text-4xl font-bold leading-none tabular-nums">{value}</p>
      <div className="min-w-0 pb-0.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
        <p className="mt-0.5 truncate text-[11px] tabular-nums text-muted-foreground/60">{sub}</p>
      </div>
    </div>
  )
}

/** What the answers were. Numbers only: every one of them is activity. */
function KindRow({ kinds, silent }: { kinds: Kinds; silent: number }) {
  const { T } = useLanguage()
  const cells: { label: string; value: number; hollow?: boolean }[] = [
    { label: T.dashboard.actPrimary, value: kinds.primary },
    { label: T.dashboard.actBench, value: kinds.bench },
    { label: T.dashboard.actTentative, value: kinds.tentative },
    { label: T.dashboard.actOut, value: kinds.out },
  ]
  // A class deleted from Settings has no kind left; shown only when it exists.
  if (kinds.other > 0) cells.push({ label: T.dashboard.actOther, value: kinds.other })
  cells.push({ label: T.dashboard.actSilent, value: silent, hollow: true })

  return (
    <div className="flex flex-wrap gap-1.5">
      {cells.map(c => (
        <div
          key={c.label}
          className={`min-w-[4.25rem] flex-1 rounded-sm border px-2 py-1.5 ${
            c.hollow ? 'border-dashed border-white/15' : 'border-white/10 bg-white/[0.035]'
          }`}
        >
          <p className={`font-display text-base font-bold leading-none tabular-nums ${
            c.hollow ? 'text-muted-foreground' : 'text-foreground/90'
          }`}>
            {c.value}
          </p>
          <p className="mt-1 truncate text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
            {c.label}
          </p>
        </div>
      ))}
    </div>
  )
}

function Legend() {
  const { T } = useLanguage()
  return (
    <div className="flex items-center gap-3 text-[10px] text-muted-foreground/60">
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-[2px]" style={{ background: ANSWERED }} />
        {T.dashboard.actAnswered}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-[2px] border border-white/25" />
        {T.dashboard.actSilent}
      </span>
    </div>
  )
}

function useDay() {
  const { lang } = useLanguage()
  const locale = lang === 'tr' ? 'tr-TR' : 'en-GB'
  return (ts: number) =>
    ts ? new Date(ts * 1000).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '-'
}

// ── Team ───────────────────────────────────────────────────────────────────────

export function TeamActivity({ data }: { data: ActivitySummary | null }) {
  const { T } = useLanguage()
  const day = useDay()

  if (!data) return <Skeleton />
  const t = data.team
  if (t.matches.length === 0 || t.members === 0) {
    return <p className="text-sm italic text-muted-foreground/50">{T.dashboard.actNoMatches}</p>
  }

  return (
    <div className="space-y-3">
      <Hero
        value={rate(t.answered, t.expected)}
        label={T.dashboard.actRate}
        sub={`${T.dashboard.actLastN.replace('{n}', String(t.matches.length))} · ${T.dashboard.actAnswers
          .replace('{a}', String(t.answered))
          .replace('{e}', String(t.expected))}`}
      />

      {/* One column per match, oldest on the left. The track is the clan called
          to that match; the fill is how much of it answered. */}
      <div className="flex items-end gap-1" style={{ height: STRIP_H }}>
        {t.matches.map(m => {
          const share = m.expected === 0 ? 0 : m.answered / m.expected
          return (
            <div
              key={m.id}
              title={`${m.title} · ${day(m.start_time)} · ${m.answered}/${m.expected}`}
              className="relative h-full min-w-0 max-w-10 flex-1 rounded-[2px] border border-white/15"
            >
              <div
                className="absolute inset-x-0 bottom-0 rounded-[1px]"
                style={{ height: `${share * 100}%`, background: ANSWERED }}
              />
            </div>
          )
        })}
      </div>
      <Legend />

      <KindRow kinds={t.kinds} silent={t.expected - t.answered} />

      <div className="grid grid-cols-2 gap-1.5 text-[11px] text-muted-foreground">
        <p className="rounded-sm border border-white/10 bg-white/[0.035] px-2.5 py-1.5">
          <span className="font-display text-sm font-bold tabular-nums text-foreground/90">{t.always}</span>{' '}
          {T.dashboard.actAlways}
        </p>
        <p className="rounded-sm border border-white/10 bg-white/[0.035] px-2.5 py-1.5">
          <span className="font-display text-sm font-bold tabular-nums text-foreground/90">{t.never}</span>{' '}
          {T.dashboard.actNever}
        </p>
      </div>
    </div>
  )
}

// ── Personal ───────────────────────────────────────────────────────────────────

export function PersonalActivity({ data }: { data: ActivitySummary | null }) {
  const { T } = useLanguage()
  const navigate = useNavigate()
  const day = useDay()

  if (!data) return <Skeleton />
  const me = data.me
  if (me.matches.length === 0) {
    return <p className="text-sm italic text-muted-foreground/50">{T.dashboard.actNoMatches}</p>
  }

  const stateLabel = (m: ActivitySummary['me']['matches'][number]) =>
    m.state === 'answered' ? (m.class_name || T.dashboard.actAnswered)
      : m.state === 'before' ? T.dashboard.actBefore
      : T.dashboard.actSilent

  return (
    <div className="space-y-3">
      <Hero
        value={rate(me.answered, me.expected)}
        label={T.dashboard.actRate}
        sub={me.expected === 0
          ? T.dashboard.actNothingYet
          : T.dashboard.actMine.replace('{a}', String(me.answered)).replace('{e}', String(me.expected))}
      />

      {/* One cell per match, oldest on the left. Same height as the team strip
          so the two cards line up across the grid row. */}
      <div className="flex items-center gap-1" style={{ height: STRIP_H }}>
        {me.matches.map(m => (
          <div
            key={m.id}
            title={`${m.title} · ${day(m.start_time)} · ${stateLabel(m)}`}
            className={`h-7 min-w-0 max-w-10 flex-1 rounded-[2px] border ${
              m.state === 'answered' ? 'border-transparent'
                : m.state === 'silent' ? 'border-white/25'
                : 'border-dashed border-white/10'
            }`}
            style={m.state === 'answered' ? { background: ANSWERED } : undefined}
          />
        ))}
      </div>
      <Legend />

      <KindRow kinds={me.kinds} silent={me.expected - me.answered} />

      <div className="grid grid-cols-2 gap-1.5 text-[11px] text-muted-foreground">
        <p className="rounded-sm border border-white/10 bg-white/[0.035] px-2.5 py-1.5">
          <span className="font-display text-sm font-bold tabular-nums text-foreground/90">{me.streak}</span>{' '}
          {T.dashboard.actStreak}
        </p>
        {me.pending.length > 0 ? (
          <button
            onClick={() => navigate(`/matches?select=${me.pending[0].id}`)}
            className="flex cursor-pointer items-center justify-between gap-1 rounded-sm border border-live/30 bg-live/10 px-2.5 py-1.5 text-left text-live transition-colors hover:bg-live/20"
          >
            <span>
              <span className="font-display text-sm font-bold tabular-nums">{me.pending.length}</span>{' '}
              {T.dashboard.actPending}
            </span>
            <ArrowRight className="h-3 w-3 shrink-0" />
          </button>
        ) : (
          <p className="rounded-sm border border-white/10 bg-white/[0.035] px-2.5 py-1.5">
            {T.dashboard.actNoPending}
          </p>
        )}
      </div>
    </div>
  )
}
